import _ from 'lodash'
import { TemporaryDataType } from '../temporary-data-table/useTemporaryDataState'

export const getAllExpandedKeys = (
    data: TemporaryDataType,
    initialExpandedRowKeys: ExpandedRowKeys,
    setExpandedRowKeysByEntity: (entity: keyof ExpandedRowKeys, keys: React.Key[]) => void
): void => {
    // Create index maps for O(1) access
    const interventionsByParent = new Map<string, typeof data.interventions>()
    const sectionsByParent = new Map<string, typeof data.sections>()
    const runsByParent = new Map<string, typeof data.runs>()
    const tubularsByParent = new Map<string, typeof data.tubulars>()
    const rigTablesByParent = new Map<string, typeof data.rigTables>()

    // Index everything in a single pass O(n)
    data.interventions.forEach(item => {
        const arr = interventionsByParent.get(String(item.parent)) || []
        arr.push(item)
        interventionsByParent.set(String(item.parent), arr)
    })
    data.sections.forEach(item => {
        const arr = sectionsByParent.get(String(item.parent)) || []
        arr.push(item)
        sectionsByParent.set(String(item.parent), arr)
    })
    data.runs.forEach(item => {
        const arr = runsByParent.get(String(item.parent)) || []
        arr.push(item)
        runsByParent.set(String(item.parent), arr)
    })
    data.tubulars.forEach(item => {
        const arr = tubularsByParent.get(String(item.parent)) || []
        arr.push(item)
        tubularsByParent.set(String(item.parent), arr)
    })
    data.rigTables.forEach(item => {
        const arr = rigTablesByParent.get(String(item.parent)) || []
        arr.push(item)
        rigTablesByParent.set(String(item.parent), arr)
    })

    const allKeys = new Set<string>()
    const allKeysByEntity = new Map<keyof ExpandedRowKeys, Set<string>>([
        ['wells', new Set<string>()],
        ['interventions', new Set<string>()],
        ['sections', new Set<string>()],
        ['runs', new Set<string>()],
        ['tubulars', new Set<string>()],
        ['rigTables', new Set<string>()]
    ])

    // DFS with direct access via indices
    const processWell = (well: typeof data.wells[0]): void => {
        allKeys.add(String(well.rowId))
        allKeysByEntity.get('wells')?.add(String(well.rowId))

        // Direct O(1) access
        const interventions = interventionsByParent.get(String(well.nodeId)) || []
        interventions.forEach(intervention => {
            processIntervention(intervention)
        })
    }

    const processIntervention = (intervention: typeof data.interventions[0]): void => {
        allKeys.add(String(intervention.rowId))
        allKeysByEntity.get('interventions')?.add(String(intervention.rowId))

        const sections = sectionsByParent.get(String(intervention.nodeId)) || []
        sections.forEach(section => processSection(section))

        const rigTables = rigTablesByParent.get(String(intervention.nodeId)) || []
        rigTables.forEach(rigTable => {
            allKeys.add(String(rigTable.rowId))
            allKeysByEntity.get('rigTables')?.add(String(rigTable.rowId))
        })
    }

    const processSection = (section: typeof data.sections[0]): void => {
        allKeys.add(String(section.rowId))
        allKeysByEntity.get('sections')?.add(String(section.rowId))

        const runs = runsByParent.get(String(section.nodeId)) || []
        runs.forEach(run => processRun(run))
    }

    const processRun = (run: typeof data.runs[0]): void => {
        allKeys.add(String(run.rowId))
        allKeysByEntity.get('runs')?.add(String(run.rowId))

        const tubulars = tubularsByParent.get(String(run.nodeId)) || []
        tubulars.forEach(tubular => {
            allKeys.add(String(tubular.rowId))
            allKeysByEntity.get('tubulars')?.add(String(tubular.rowId))
        })
    }

    data.wells.forEach(processWell)

    expandRowKeysByEntityByHierarchy(initialExpandedRowKeys, setExpandedRowKeysByEntity, {
        wells: Array.from(allKeysByEntity.get('wells') || []) as React.Key[],
        interventions: Array.from(allKeysByEntity.get('interventions') || []) as React.Key[],
        sections: Array.from(allKeysByEntity.get('sections') || []) as React.Key[],
        runs: Array.from(allKeysByEntity.get('runs') || []) as React.Key[],
        tubulars: Array.from(allKeysByEntity.get('tubulars') || []) as React.Key[],
        rigTables: Array.from(allKeysByEntity.get('rigTables') || []) as React.Key[]
    })
}

function expandRowKeysByEntityByHierarchy(
    initialExpandedRowKeys: ExpandedRowKeys,
    setExpandedRowKeysByEntity: (entity: keyof ExpandedRowKeys, keys: React.Key[]) => void,
    allKeysByEntityToBeAdded: ExpandedRowKeys
): void {
    const hierarchicalEntities: (keyof ExpandedRowKeys)[] = [
        'wells',
        'interventions',
        'sections',
        'runs',
        'tubulars',
        'rigTables'
    ]
    let expandedRowKeys = initialExpandedRowKeys
    const chunkSize = 3
    let index = 0
    const tryExpandNextEntity = (): void => {
        if (index >= hierarchicalEntities.length) {
            index = 0
            expandedRowKeys = initialExpandedRowKeys

            return
        }

        const entity = hierarchicalEntities[index]
        const keysToAdd = allKeysByEntityToBeAdded[entity].filter(key => !expandedRowKeys[entity].includes(key))
        index++

        if (keysToAdd.length > 0 && keysToAdd.length <= chunkSize) {
            expandedRowKeys[entity] = [...expandedRowKeys[entity], ...keysToAdd]
            setExpandedRowKeysByEntity(entity, expandedRowKeys[entity])
        }

        if (keysToAdd.length > chunkSize) {
            expandedRowKeys[entity] = [...expandedRowKeys[entity], ...keysToAdd]
            setExpandedRowKeysByEntity(entity, expandedRowKeys[entity])
            // Update remaining keys for next iteration
            allKeysByEntityToBeAdded[entity] = keysToAdd.slice(chunkSize)
            // Keep the index to reprocess the same entity on next iteration
            index--
        }

        _.debounce(tryExpandNextEntity, 100, { leading: false, trailing: true })()
    }

    tryExpandNextEntity()
}
